import { RequestedByType } from '@types';
import React, { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';

type RemoveWaiverModalProps = {
  isOpen: boolean;
  isRemoving: boolean;
  onClose: () => void;
  onConfirm: (requestedByType: RequestedByType, notes: string) => void;
};

export const RemoveWaiverModal: React.FC<RemoveWaiverModalProps> = ({
  isOpen,
  isRemoving,
  onClose,
  onConfirm,
}) => {
  const [requestedByType, setRequestedByType] =
    useState<RequestedByType>('Staff');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setRequestedByType('Staff');
      setNotes('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Remove Waiver</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            type="button"
          >
            <MdClose size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            This removes the waiver from this booking. The waiver file is kept,
            and a new waiver will need to be signed or attached.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Requested By <span className="text-red-600">*</span>
            </label>
            <div className="flex gap-2">
              {(['Customer', 'Staff'] as RequestedByType[]).map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setRequestedByType(option)}
                  className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    requestedByType === option
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label
              htmlFor="removeWaiverNotes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notes <span className="text-gray-500">(Optional)</span>
            </label>
            <textarea
              id="removeWaiverNotes"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              maxLength={500}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              placeholder="Why this waiver is being removed..."
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(requestedByType, notes.trim())}
            disabled={isRemoving}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isRemoving
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            {isRemoving ? 'Removing...' : 'Remove Waiver'}
          </button>
        </div>
      </div>
    </div>
  );
};
